import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  ThemeProvider,
  createTheme,
  Tabs,
  Tab,
  TablePagination,
  Avatar,
  Stack,
  Badge,
  alpha,
} from '@mui/material';
import {
  Bot,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  PhoneOff,
} from 'lucide-react';
import { mockLeads } from '@/data/mock-leads';
import { mockCallHistory } from '@/data/mock-call-history';
import { Lead } from '@/types/lead';
import { CallHistory } from '@/types/call-history';

const theme = createTheme({
  palette: {
    primary: {
      main: '#003F6C',
    },
    secondary: {
      main: '#ED1C24',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
});

const getStatusColor = (status: Lead['status']) => {
  const colors = {
    new: 'default',
    contacted: 'primary',
    qualified: 'warning',
    interested: 'success',
  } as const;
  return colors[status];
};

const getOutcomeIcon = (outcome: CallHistory['outcome']) => {
  switch (outcome) {
    case 'interested':
      return <CheckCircle size={16} color="#4caf50" />;
    case 'not_interested':
      return <XCircle size={16} color="#f44336" />;
    case 'no_answer':
      return <PhoneOff size={16} color="#666" />;
    default:
      return <Phone size={16} color="#2196f3" />;
  }
};

const formatDuration = (seconds: number) => {
  if (seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-SG', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LeadsList: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleCall = (lead: Lead) => {
    const product = lead.preferredProduct || 'prushield';
    window.open(`/agent/call?product=${product}&leadId=${lead.id}`, '_blank');
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header with AI branding */}
          <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 4 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: '#4caf50',
                    borderRadius: '50%',
                    border: '2px solid white',
                  }}
                />
              }
            >
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                <Bot size={32} />
              </Avatar>
            </Badge>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                AI Sales Agent Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Automated cold calling for Prudential insurance products
              </Typography>
            </Box>
          </Stack>

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
            >
              <Tab label="New Leads" />
              <Tab label="Call History" />
            </Tabs>
          </Paper>

          {/* New Leads Tab */}
          {tabValue === 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ borderRadius: 2 }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <TableCell>
                      <strong>Contact</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Company</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Industry</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Product Match</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Pain Points</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>AI Action</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockLeads
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((lead) => (
                      <TableRow
                        key={lead.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.02,
                            ),
                          },
                          '& td': { py: 2.5 },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {lead.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {lead.title}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {lead.company}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {lead.companySize} employees
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{lead.industry}</TableCell>
                        <TableCell>
                          <Chip
                            label={lead.status.replace('_', ' ')}
                            size="small"
                            color={getStatusColor(lead.status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {lead.preferredProduct && (
                            <Chip
                              label={lead.preferredProduct.toUpperCase()}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 200 }}>
                            {lead.painPoints?.slice(0, 2).map((point, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                • {point}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            startIcon={<Bot size={16} />}
                            onClick={() => handleCall(lead)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              px: 2,
                            }}
                          >
                            AI Call
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={mockLeads.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

          {/* Call History Tab */}
          {tabValue === 1 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ borderRadius: 2 }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <TableCell>
                      <strong>Date & Time</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Contact</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Duration</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Outcome</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Product</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Key Points</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Next Action</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockCallHistory
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((call) => (
                      <TableRow
                        key={call.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.02,
                            ),
                          },
                          '& td': { py: 2.5 },
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Clock size={14} color="#666" />
                            <Typography variant="body2">
                              {formatDate(call.callDate)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {call.leadName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {call.company}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDuration(call.duration)}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {getOutcomeIcon(call.outcome)}
                            <Typography
                              variant="body2"
                              sx={{ textTransform: 'capitalize' }}
                            >
                              {call.outcome.replace('_', ' ')}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={call.product.toUpperCase()}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 250 }}>
                            {call.keyPoints.slice(0, 2).map((point, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                • {point}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {call.nextAction && (
                            <Box>
                              <Typography variant="caption" color="primary">
                                {call.nextAction}
                              </Typography>
                              {call.callbackDate && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {formatDate(call.callbackDate)}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={mockCallHistory.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LeadsList;
